import type { ContentItem, Doc, EditorSchema, MarkItem } from "@smartsuite/smartdoc";

/**
 * Node and mark names the widget's schema does not know about make
 * `ProseMirrorNode.fromJSON` throw, which happens inside the editor's mount effect and
 * therefore takes the whole widget down through the error boundary. The document itself
 * is injected by the native app and is never validated on the way in, so anything a
 * different producer wrote reaches ProseMirror as-is.
 *
 * This reconciles an incoming document with the schema the active preset actually
 * produces. It is intentionally lossy: showing the parts of a document we understand
 * beats showing nothing at all.
 */

/**
 * Marks that other producers write under a different name than the schema uses.
 * Renaming keeps the formatting instead of dropping it.
 */
const LEGACY_MARK_ALIASES: Record<string, string> = {
  bold: "strong",
  italic: "em",
  strike: "strikethrough",
};

export interface SanitizeDocReport {
  /** Node types dropped because the schema has no such node. */
  unknownNodeTypes: string[];
  /** Mark types dropped because the schema has no such mark. */
  unknownMarkTypes: string[];
  /** Mark types renamed to their schema equivalent, as `from -> to`. */
  renamedMarkTypes: string[];
  /** Node types dropped because required attributes were missing. */
  invalidNodeTypes: string[];
  /** Mark types dropped because required attributes were missing. */
  invalidMarkTypes: string[];
  /** Number of zero-length text nodes dropped. */
  emptyTextNodeCount: number;
  /** Set when the sanitized document still failed to parse and had to be discarded. */
  parseError?: string;
}

export interface SanitizeDocResult {
  doc: Doc | undefined;
  /** `true` when the document was left exactly as it came in. */
  isUnchanged: boolean;
  report: SanitizeDocReport;
}

interface Collector {
  unknownNodeTypes: Set<string>;
  unknownMarkTypes: Set<string>;
  renamedMarkTypes: Set<string>;
  invalidNodeTypes: Set<string>;
  invalidMarkTypes: Set<string>;
  emptyTextNodeCount: number;
  changed: boolean;
}

/**
 * An attribute is required when its spec declares no default - the same rule
 * prosemirror-model applies in `computeAttrs`, which is what throws
 * "No value supplied for attribute ...". Read from `spec.attrs` rather than the
 * `NodeType.attrs` map, which is not part of the published types.
 */
const hasMissingRequiredAttrs = (
  attrSpecs: Record<string, { default?: unknown }> | undefined,
  attrs: Record<string, unknown> | undefined
): boolean =>
  Object.keys(attrSpecs ?? {}).some(
    (name) =>
      !Object.prototype.hasOwnProperty.call(attrSpecs?.[name] ?? {}, "default") &&
      (attrs === undefined || attrs[name] === undefined)
  );

/**
 * A node with no usable `type` is dropped, and its type is reported as a placeholder
 * naming the shape that arrived - recording the raw value serialised to `null`, which
 * said nothing about what the producer actually sent.
 */
const describeNodeType = (node: unknown): string => {
  if (!node || typeof node !== "object") {
    return `<${node === null ? "null" : typeof node}>`;
  }

  const type = (node as { type?: unknown }).type;

  return type === undefined || type === null ? "<missing type>" : `<${typeof type} type>`;
};

const sanitizeMarks = (
  marks: MarkItem[] | undefined,
  schema: EditorSchema,
  collector: Collector
): MarkItem[] | undefined => {
  if (!marks?.length) {
    return marks;
  }

  const sanitized: MarkItem[] = [];

  for (const mark of marks) {
    const originalType = String(mark?.type ?? "");
    const type = LEGACY_MARK_ALIASES[originalType] ?? originalType;
    const markType = schema.marks[type as keyof typeof schema.marks];

    if (!markType) {
      collector.unknownMarkTypes.add(originalType);
      collector.changed = true;
      continue;
    }

    if (hasMissingRequiredAttrs(markType.spec.attrs, mark.attrs)) {
      collector.invalidMarkTypes.add(type);
      collector.changed = true;
      continue;
    }

    if (type !== originalType) {
      collector.renamedMarkTypes.add(`${originalType} -> ${type}`);
      collector.changed = true;
    }

    sanitized.push(type === originalType ? mark : ({ ...mark, type } as MarkItem));
  }

  // Reference equality is what tells the caller a subtree is untouched, which is what
  // keeps an unmodified document `===` its input.
  const isUnchanged =
    sanitized.length === marks.length && sanitized.every((mark, index) => mark === marks[index]);

  return isUnchanged ? marks : sanitized;
};

/**
 * Sanitize one node. Returns the node's replacement in its parent: a single node, or a
 * list when an unusable node is dropped but its children can be lifted into its place.
 */
const sanitizeNode = (
  node: ContentItem,
  schema: EditorSchema,
  collector: Collector
): ContentItem[] => {
  if (!node || typeof node !== "object" || typeof node.type !== "string") {
    collector.unknownNodeTypes.add(describeNodeType(node));
    collector.changed = true;
    return [];
  }

  if (node.type === "text") {
    // ProseMirror forbids zero-length text nodes outright.
    if (!node.text) {
      collector.emptyTextNodeCount += 1;
      collector.changed = true;
      return [];
    }

    const marks = sanitizeMarks(node.marks, schema, collector);

    return [marks === node.marks ? node : { ...node, marks }];
  }

  const nodeType = schema.nodes[node.type as keyof typeof schema.nodes];
  const content = sanitizeContent(node.content, node.type, schema, collector);

  if (!nodeType) {
    collector.unknownNodeTypes.add(node.type);
    collector.changed = true;

    // Lift whatever we could salvage into the parent rather than losing the subtree.
    return content ?? [];
  }

  if (hasMissingRequiredAttrs(nodeType.spec.attrs, node.attrs)) {
    collector.invalidNodeTypes.add(node.type);
    collector.changed = true;

    return content ?? [];
  }

  const marks = sanitizeMarks(node.marks, schema, collector);

  if (marks === node.marks && content === node.content) {
    return [node];
  }

  return [{ ...node, ...(content ? { content } : {}), ...(marks ? { marks } : {}) }];
};

const sanitizeContent = (
  content: ContentItem[] | undefined,
  parentType: string,
  schema: EditorSchema,
  collector: Collector
): ContentItem[] | undefined => {
  if (!Array.isArray(content)) {
    return content;
  }

  const sanitized = content.flatMap((child) => sanitizeNode(child, schema, collector));

  if (
    sanitized.length === content.length &&
    sanitized.every((child, index) => child === content[index])
  ) {
    return content;
  }

  collector.changed = true;

  return wrapStrayInlineContent(sanitized, parentType, schema);
};

/**
 * Lifting the children of a dropped node can leave inline content directly inside a block
 * container - text nodes in the doc, for instance. Wrap those runs in a paragraph so the
 * result still matches the schema's content model.
 */
const wrapStrayInlineContent = (
  content: ContentItem[],
  parentType: string,
  schema: EditorSchema
): ContentItem[] => {
  const parentNodeType = schema.nodes[parentType as keyof typeof schema.nodes];
  const paragraph = schema.nodes.paragraph;

  if (!parentNodeType || parentNodeType.inlineContent || !paragraph) {
    return content;
  }

  const wrapped: ContentItem[] = [];
  let inlineRun: ContentItem[] = [];

  const flushInlineRun = (): void => {
    if (inlineRun.length) {
      wrapped.push({ type: "paragraph", content: inlineRun } as ContentItem);
      inlineRun = [];
    }
  };

  for (const child of content) {
    const childType = schema.nodes[child.type as keyof typeof schema.nodes];

    if (childType?.isInline) {
      inlineRun.push(child);
      continue;
    }

    flushInlineRun();
    wrapped.push(child);
  }

  flushInlineRun();

  return wrapped;
};

/**
 * Short, stable names for each category of change, in the order the report declares them.
 * These go into Sentry tags and fingerprints, so keep them stable across releases.
 */
export type SanitizeDocReason =
  | "unknown-node"
  | "unknown-mark"
  | "renamed-mark"
  | "invalid-node"
  | "invalid-mark"
  | "empty-text"
  | "parse-error";

export const getSanitizeDocReasons = (report: SanitizeDocReport): SanitizeDocReason[] => {
  const reasons: SanitizeDocReason[] = [];

  if (report.unknownNodeTypes.length) reasons.push("unknown-node");
  if (report.unknownMarkTypes.length) reasons.push("unknown-mark");
  if (report.renamedMarkTypes.length) reasons.push("renamed-mark");
  if (report.invalidNodeTypes.length) reasons.push("invalid-node");
  if (report.invalidMarkTypes.length) reasons.push("invalid-mark");
  if (report.emptyTextNodeCount > 0) reasons.push("empty-text");
  if (report.parseError !== undefined) reasons.push("parse-error");

  return reasons;
};

/** The result for a document that needed no reconciliation at all. */
export const unchangedSanitizeDocResult = (doc: Doc | undefined): SanitizeDocResult => ({
  doc,
  isUnchanged: true,
  report: {
    unknownNodeTypes: [],
    unknownMarkTypes: [],
    renamedMarkTypes: [],
    invalidNodeTypes: [],
    invalidMarkTypes: [],
    emptyTextNodeCount: 0,
  },
});

export const isSanitizeDocReportEmpty = (report: SanitizeDocReport): boolean =>
  report.unknownNodeTypes.length === 0 &&
  report.unknownMarkTypes.length === 0 &&
  report.renamedMarkTypes.length === 0 &&
  report.invalidNodeTypes.length === 0 &&
  report.invalidMarkTypes.length === 0 &&
  report.emptyTextNodeCount === 0 &&
  report.parseError === undefined;

/**
 * Reconcile an incoming document with `schema`, renaming or dropping anything the schema
 * cannot represent. Returns the original object untouched when nothing had to change, so the
 * common case costs nothing downstream.
 */
export const sanitizeDoc = (
  doc: Doc | undefined,
  schema: EditorSchema | undefined
): SanitizeDocResult => {
  const collector: Collector = {
    unknownNodeTypes: new Set(),
    unknownMarkTypes: new Set(),
    renamedMarkTypes: new Set(),
    invalidNodeTypes: new Set(),
    invalidMarkTypes: new Set(),
    emptyTextNodeCount: 0,
    changed: false,
  };

  const toResult = (sanitized: Doc | undefined): SanitizeDocResult => ({
    doc: sanitized,
    isUnchanged: !collector.changed,
    report: {
      unknownNodeTypes: Array.from(collector.unknownNodeTypes),
      unknownMarkTypes: Array.from(collector.unknownMarkTypes),
      renamedMarkTypes: Array.from(collector.renamedMarkTypes),
      invalidNodeTypes: Array.from(collector.invalidNodeTypes),
      invalidMarkTypes: Array.from(collector.invalidMarkTypes),
      emptyTextNodeCount: collector.emptyTextNodeCount,
    },
  });

  // Without a schema there is nothing to reconcile against - hand the document back and
  // let the editor decide, exactly as before this sanitizer existed.
  if (!doc || !schema) {
    return toResult(doc);
  }

  if (typeof doc.type !== "string" || !schema.nodes[doc.type as keyof typeof schema.nodes]) {
    collector.unknownNodeTypes.add(typeof doc.type === "string" ? doc.type : describeNodeType(doc));
    collector.changed = true;

    return toResult(undefined);
  }

  const content = sanitizeContent(doc.content, doc.type, schema, collector);

  if (!collector.changed) {
    return toResult(doc);
  }

  return toResult({ ...doc, content: content ?? [] });
};

/**
 * Sanitize a document and then prove the result actually parses.
 *
 * The parse is the guarantee that matters: `sanitizeDoc` covers the mismatches we know
 * about, but the editor calls `ProseMirrorNode.fromJSON` inside its mount effect, where
 * anything still unhandled becomes a blank screen behind the error boundary. Verifying
 * here turns that into an empty editor plus a report - recoverable, and visible.
 *
 * Use this at every point where an incoming document meets the schema.
 */
export const toRenderableDoc = (
  doc: Doc | undefined,
  schema: EditorSchema | undefined
): SanitizeDocResult => {
  const unchanged = unchangedSanitizeDocResult(doc);

  if (!doc || !schema) {
    return unchanged;
  }

  // `schema.nodeFromJSON` is the same `Node.fromJSON` the editor reaches, bound to this
  // schema instance - no second copy of prosemirror-model involved.
  try {
    // Fast path. Almost every document is already valid, and this is the very parse the
    // editor is about to do anyway - so try it first and keep the reconciliation walk off
    // the common path entirely. It also fails fast, throwing at the first bad node rather
    // than walking the whole tree.
    schema.nodeFromJSON(doc);

    return unchanged;
  } catch {
    // Fall through and reconcile - the document is one we would otherwise crash on.
  }

  const result = sanitizeDoc(doc, schema);

  if (!result.doc) {
    return result;
  }

  try {
    schema.nodeFromJSON(result.doc);

    return result;
  } catch (error) {
    return {
      doc: undefined,
      isUnchanged: false,
      report: {
        ...result.report,
        parseError: error instanceof Error ? error.message : String(error),
      },
    };
  }
};
